import React from "react";

/**
 * Erro-boundary global. Captura qualquer exceção de renderização em
 * qualquer componente filho e troca a árvore quebrada por uma tela de
 * recuperação. Sem isso, com React 18 + StrictMode, um erro não tratado
 * desmonta o root inteiro e o usuário vê uma TELA BRANCA (caso típico
 * reportado em celulares de clientes clicando num serviço). A tela de
 * recuperação oferece:
 *   - mensagem clara em pt-BR
 *   - botão "Tentar novamente" (reseta o estado do boundary)
 *   - botão "Recarregar" que força um reload limpando caches e
 *     chamando `unregister()` em qualquer service worker — resolve
 *     também o caso raro de SW antigo servindo bundle quebrado.
 *
 * Também loga a stack no console pra ficar visível em debug remoto.
 */
export default class ErrorBoundary extends React.Component {
  constructor(props) {
    super(props);
    this.state = { hasError: false, err: null };
  }

  static getDerivedStateFromError(err) {
    return { hasError: true, err };
  }

  componentDidCatch(err, info) {
    try {
      // eslint-disable-next-line no-console
      console.error("[ErrorBoundary] render crash:", err, info?.componentStack);
    } catch (_) { /* noop */ }
    // Caso raro específico Android: o Google Translate reparenta text nodes
    // internos de um ancestral e, ao próximo render, o React tenta remover
    // um filho que o Translate "roubou". O erro é "Failed to execute
    // 'removeChild' on 'Node'". Como o fallback já é mostrado, agendamos um
    // reload automático único (via sessionStorage) para que o próprio
    // boundary não precise que o usuário aperte "Recarregar". Guardamos uma
    // flag pra evitar loop infinito caso o erro aconteça pós-reload também.
    try {
      const msg = String(err?.message || "");
      if (/removeChild|insertBefore/i.test(msg)) {
        const KEY = "_eb_dom_reload_once";
        if (!sessionStorage.getItem(KEY)) {
          sessionStorage.setItem(KEY, "1");
          setTimeout(() => { try { window.location.reload(); } catch (_) {} }, 400);
        }
      }
    } catch (_) { /* noop */ }
  }

  _reset = () => this.setState({ hasError: false, err: null });

  _hardReload = async () => {
    try {
      if ("caches" in window) {
        const keys = await caches.keys();
        await Promise.all(keys.map((k) => caches.delete(k)));
      }
    } catch (_) { /* noop */ }
    try {
      if ("serviceWorker" in navigator) {
        const regs = await navigator.serviceWorker.getRegistrations();
        await Promise.all(regs.map((r) => r.unregister()));
      }
    } catch (_) { /* noop */ }
    window.location.reload();
  };

  render() {
    if (!this.state.hasError) return this.props.children;
    const msg = String(this.state.err?.message || this.state.err || "Erro desconhecido");
    return (
      <div data-testid="error-boundary" className="min-h-screen flex items-center justify-center bg-background p-6">
        <div className="max-w-md w-full surface p-8 text-center space-y-4">
          <div className="text-5xl">⚠️</div>
          <h1 className="text-xl font-display font-bold">Algo deu errado nesta tela</h1>
          <p className="text-sm text-muted-foreground">
            Encontramos um problema ao carregar o conteúdo. Isso costuma acontecer quando o app
            está desatualizado no seu celular. Toque em <strong>Recarregar</strong> para
            buscar a versão mais recente.
          </p>
          <details className="text-left text-xs text-muted-foreground">
            <summary className="cursor-pointer">Detalhes técnicos</summary>
            <pre className="whitespace-pre-wrap break-all mt-2 opacity-80">{msg}</pre>
          </details>
          <div className="flex gap-2 justify-center pt-2">
            <button
              type="button"
              onClick={this._reset}
              data-testid="eb-retry"
              className="px-4 h-10 rounded-md border border-border text-sm"
            >Tentar novamente</button>
            <button
              type="button"
              onClick={this._hardReload}
              data-testid="eb-hard-reload"
              className="px-4 h-10 rounded-md bg-primary text-primary-foreground text-sm font-medium"
            >Recarregar app</button>
          </div>
        </div>
      </div>
    );
  }
}
